import { PlusOutlined } from '@ant-design/icons'
import { useMutation, useQuery } from '@apollo/client'
import {
  AddSheetCustomModelPropertyDocument,
  FetchSheetCustomModelsDocument,
  type ViewFieldsFragment,
} from '@salescore/client-api'
import { getOrganizationIdFromPath, Posthog, POSTHOG_EVENTS, posthogTrackView } from '@salescore/client-base'
import { useMe } from '@salescore/client-common'
import type {
  CompiledFormViewConfig,
  CompiledKpiPivotViewConfig,
  CompiledKpiViewConfig,
  CompiledSheetViewConfig,
  ModelProperty,
  NodePropertyName,
  ViewConfigTreeNode,
  ViewQueryList,
} from '@salescore/core'
// eslint-disable-next-line sonarjs/deprecation
import { disableOnClickOutsideProperties, useBooleanState } from '@salescore/frontend-common'
import { Alert, Button, Form, Input, message, Select } from 'antd'
import { useForm } from 'antd/es/form/Form'
import { Array, pipe } from 'effect'
import { isNotNullish } from 'effect/Predicate'
import { t } from 'i18next'
import { useEffect, useState } from 'react'
import { useRecoilState, useRecoilValue } from 'recoil'

import { useSheetColumns } from '../../../../recoil/selectors/useSheetColumns'
import { sheetCustomModelsAtom, viewsContextAtom } from '../../../../recoil/view/atoms'
import type { ReferenceToPropertyForAddChildNodeMutation } from '../findSameReferenceToChild'

type AvailableType = 'string' | 'text' | 'numeric' | 'date' | 'datetime' | 'boolean' | 'options'
const options: Array<{
  label: string
  value: AvailableType
}> = [
  {
    label: t(`テキスト`),
    value: 'string',
  },
  {
    label: t(`テキスト(複数行)`),
    value: 'text',
  },
  { value: 'numeric', label: t(`数値`) },
  { value: 'date', label: t(`日付`) },
  { value: 'datetime', label: t(`日時`) },
  { value: 'boolean', label: t(`チェックボックス`) },
  { value: 'options', label: t(`選択肢`) },
]

interface FormValue {
  label: string
  type: AvailableType
  selectOptionsText: string[] | undefined
}

// eslint-disable-next-line sonarjs/prefer-read-only-props
export function SheetCustomModelPropertyForm({
  node,
  parentNode,
  addNodes,
  compiledConfig,
  view,
  query,
}: {
  node: ViewConfigTreeNode
  parentNode: ViewConfigTreeNode | undefined
  addNodes: ({
    toggleNodeProperties,
    referenceToProperty,
    option,
  }: {
    toggleNodeProperties?: NodePropertyName[]
    referenceToProperty?: ReferenceToPropertyForAddChildNodeMutation
    option?: { skipSetCurrentNodePath?: boolean }
  }) => void
  compiledConfig: CompiledSheetViewConfig | CompiledKpiViewConfig | CompiledKpiPivotViewConfig | CompiledFormViewConfig
  view: ViewFieldsFragment
  query: ViewQueryList
}) {
  const context = useRecoilValue(viewsContextAtom)
  const [sheetCustomModels, setSheetCustomModels] = useRecoilState(sheetCustomModelsAtom)
  const sheetColumns = useSheetColumns(compiledConfig, query)
  const targetNode = sheetCustomModels.map((x) => x.name).includes(node.name) ? parentNode : node
  const [add] = useMutation(AddSheetCustomModelPropertyDocument)
  const [form] = useForm<FormValue>()
  const [type, setType] = useState<undefined | AvailableType>(options[0]?.value)
  // eslint-disable-next-line sonarjs/deprecation
  const loading = useBooleanState()
  const me = useMe()
  const fetchSheetCustomModelsQuery = useQuery(FetchSheetCustomModelsDocument, {
    // refetchのみで使う
    variables: {
      organizationId: me.organization.id,
      viewId: view.id,
    },
  })

  useEffect(() => {
    posthogTrackView(POSTHOG_EVENTS.view_sheet_custom_model_property_form, {
      viewId: view.id,
      prevViewId: context.prevView?.id,
      prevViewType: context.prevView?.type,
      nodeName: targetNode?.name,
    })
  }, [])

  if (targetNode === undefined) {
    // ありえないはず
    return <></>
  }

  return (
    <div
      onKeyDown={(e) => {
        e.stopPropagation()
      }}
    >
      <Form<FormValue>
        layout="vertical"
        form={form}
        className="p-4"
        initialValues={{
          type: options[0]?.value,
        }}
        // eslint-disable-next-line @typescript-eslint/no-misused-promises,@typescript-eslint/strict-void-return
        onFinish={async (value) => {
          const converted = convertType(value.type, value.selectOptionsText)
          try {
            loading.setTrue()
            const { data: result } = await add({
              variables: {
                organizationId: getOrganizationIdFromPath(),
                input: {
                  viewId: view.id,
                  nodeName: targetNode.name,
                  label: value.label,
                  type: converted.type,
                  meta: converted.meta,
                  selectOptions: converted.selectOptions,
                  // XXX: カスタムカラムの追加を行うタイミングで、ビューの情報を参照して不要となったテーブル・カラムの削除処理を行なっている。
                  //      これは、列削除のタイミングでsheetCustomModelの更新処理を行うのがやや怖かったため、この設計になっている。
                  //      2023/11以前はビューのconfigの変更はバックエンド側でも即時保存されていたが、これ以降より変更は即時保存されなくなり、
                  //      現在のclient側でのビューの情報と、server側でのビューの情報が一致しなくなる形になった。
                  //      上記の削除処理を行う際に、最新のビューのカラム情報が必要なので、これを引数で渡している
                  currentViewConfigFieldProperties: sheetColumns
                    .map((x) => x.configField?.property)
                    .filter(isNotNullish),
                },
              },
            })
            const r = result?.addSheetCustomModelPropertyV2
            if (r === undefined) {
              // eslint-disable-next-line sonarjs/todo-tag
              void message.error(t(`追加に失敗しました`)) // TODO
              return
            }
            const { sheetCustomModel, addedProperty } = r
            // modelの内容を更新
            setSheetCustomModels((xs) => Array.dedupeWith([sheetCustomModel, ...xs], (a, b) => a.name === b.name)) // 既にあれば、先頭が優先されるので更新される
            addNodes({
              referenceToProperty:
                node.modelName === sheetCustomModel.name
                  ? undefined
                  : {
                      // 既にノードがあればそのまま追加、なければ新規作成して追加
                      parentNodeName: node.name,
                      nodeName: sheetCustomModel.name,
                      modelName: sheetCustomModel.name,
                      propertyName: 'parent_id',
                      referenceTo: {
                        modelName: node.modelName,
                      },
                    },
              option: { skipSetCurrentNodePath: true },
              toggleNodeProperties: [
                {
                  nodeName: sheetCustomModel.name,
                  modelName: sheetCustomModel.name,
                  propertyName: addedProperty.name,
                },
              ],
            })
            // ここまででモデルの内容は更新されているが、GraphQLのレイヤーに残っているキャッシュをクリアするためにrefetchする
            // （本来であれば追加のmutationのレスポンスで更新されるはずだが、現状の実装ではそうなっていない？）
            await fetchSheetCustomModelsQuery.refetch()

            void message.success(t(`カスタムカラムを作成しました`))
            Posthog.track(POSTHOG_EVENTS.create_sheet_custom_model_property, {
              viewId: view.id,
              nodeName: targetNode.name,
              label: value.label,
              type: converted.type,
              meta: converted.meta,
              selectOptions: converted.selectOptions,
            })
            form.resetFields()
          } catch (error) {
            if (error instanceof Error) {
              void message.error(`${t(`エラーが発生しました。`)}${error.message}`)
            } else {
              void message.error(t(`エラーが発生しました。`))
            }
          } finally {
            loading.setFalse()
          }
        }}
      >
        <Form.Item
          name="label"
          required
          label={t(`ラベル`)}
          rules={[{ required: true, message: t(`入力してください`) }]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          name="type"
          required
          label={t(`データ型`)}
          rules={[{ required: true, message: t(`入力してください`) }]}
        >
          <Select
            {...disableOnClickOutsideProperties}
            options={options}
            onChange={(value) => {
              // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
              setType(value as AvailableType)
            }}
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
            }}
          />
        </Form.Item>
        {type === 'options' && (
          <Form.Item
            name="selectOptionsText"
            required
            label={t(`選択肢`)}
            rules={[{ required: true, message: t(`入力してください`) }]}
          >
            <Select
              mode="tags"
              style={{ width: '100%' }}
              placeholder={t(`選択肢を入力`)}
              onChange={(values: string[]) => {
                // 選択肢に前後の空白がある場合は削除し、空文字の選択肢は削除する
                form.setFieldsValue({
                  selectOptionsText: pipe(
                    values,
                    // eslint-disable-next-line sonarjs/super-linear-regex
                    Array.map((value) => value.replaceAll(/^\s+|\s+$/gv, '')),
                    Array.filter((v) => v !== ''),
                    Array.dedupe,
                  ),
                })
              }}
            />
          </Form.Item>
        )}
        <div className="flex flex-row-reverse">
          <Form.Item>
            <Button type="primary" htmlType="submit" icon={<PlusOutlined />} loading={loading.isTrue}>
              {t(`追加`)}
            </Button>
          </Form.Item>
        </div>
        <Alert
          message={t('このシート「{{viewName}}」専用に作成される列です。シートを削除した場合、列も削除されます。', {
            viewName: view.name,
          })}
          type="info"
          showIcon
        />
      </Form>
    </div>
  )
}

function convertType(
  type: AvailableType,
  selectOptionsText: string[] | undefined,
): Pick<ModelProperty, 'type' | 'selectOptions' | 'meta'> {
  switch (type) {
    case 'string': {
      return {
        type: 'string',
      }
    }
    case 'text': {
      return {
        type: 'string',
        meta: 'text',
      }
    }
    case 'numeric': {
      return {
        type: 'numeric',
      }
    }
    case 'date': {
      return {
        type: 'date',
      }
    }
    case 'datetime': {
      return {
        type: 'datetime',
      }
    }
    case 'boolean': {
      return {
        type: 'boolean',
      }
    }
    case 'options': {
      // eslint-disable-next-line sonarjs/todo-tag
      // TODO: selectOptionsの判定
      return {
        type: 'string',
        selectOptions: selectOptionsText?.map((value) => ({ value, label: value })) ?? [],
      }
    }
  }
}
